import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  Suspense,
} from "react";

import {
  Grid,
  Box,
  HStack,
  Text,
  VStack,
  Flex,
  Button,
  Icon
} from "@chakra-ui/react";

import { BsCart4, BsFillGeoAltFill } from "react-icons/bs";
import { FiCheckCircle as CheckCircleIcon } from "react-icons/fi";

import { useDispatch, useSelector } from "react-redux";
import {
  updateLocalCart,
  fetchCartFromServer,
  clearLocalCartData,
  updateItemQuantityServer,
} from "../../slices/cart";

import { createOrder } from "../../slices/orders";
import { useNavigate } from "react-router-dom";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Load Razorpay
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// Lazy loaded components
const Bag = React.lazy(() => import("./Bag"));
const Address = React.lazy(() => import("./Address"));
const OrderSummary = React.lazy(() => import("../cart1/OrderSummary"));

const CART_ACTION = {
  ADD: "ADD",
  SET_QUANTITY: "SET_QUANTITY",
  REMOVE: "REMOVE",
};

const TABS = [
  { label: "Bag", icon: BsCart4, component: Bag },
  { label: "Address", icon: BsFillGeoAltFill, component: Address },
];

const CheckoutStepper = ({ tabs, activeTab }) => {
  const activeIndex = tabs.findIndex((t) => t.label === activeTab);

  return (
    <HStack spacing={10} align="stretch" w="full">
      {tabs.map((tab, index) => (
        <HStack
          key={index}
          spacing={3}
          p={3}
          w="full"
          bg={index <= activeIndex ? "blue.50" : "white"}
          color={index <= activeIndex ? "blue.700" : "gray.500"}
          borderRadius="lg"
          borderLeft={
            index <= activeIndex
              ? "4px solid #3182CE"
              : "4px solid #E2E8F0"
          }
          boxShadow={index === activeIndex ? "md" : "none"}
        >
          <Flex
            w="30px"
            h="30px"
            borderRadius="full"
            bg={index <= activeIndex ? "#3182CE" : "gray.300"}
            color="white"
            align="center"
            justify="center"
            fontWeight="bold"
            fontSize="sm"
          >
            {index < activeIndex ? (
              <Icon as={CheckCircleIcon} boxSize={5} />
            ) : (
              index + 1
            )}
          </Flex>

          <Text fontWeight={index === activeIndex ? "bold" : "medium"} fontSize="lg">
            {tab.label}
          </Text>
        </HStack>
      ))}
    </HStack>
  );
};

export default function Checkout() {
  const [activeTab, setActiveTab] = useState(TABS[0].label);
  const [selectedAddress, setSelectedAddress] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    items: cartItems,
    final_total,
    total,
    discount,
    shipping_fee,
    status: cartStatus
  } = useSelector((state) => state.cart);

  console.log(cartItems);

  const { createStatus } = useSelector((state) => state.orders);

  const { userLoggedIn } = useSelector((state) => state.auth);

  const { addresses } = useSelector((state) => state.userAddress);

  const [coupon, setCoupon] = useState("");
  const [giftWrap, setGiftWrap] = useState(false);

  useEffect(() => {
    if (userLoggedIn && cartStatus === "idle") {
      dispatch(fetchCartFromServer());
    }
  }, [userLoggedIn, dispatch]);

  useEffect(() => {
    if (!addresses || addresses.length === 0) return;

    const defaultAddress = addresses.find((a) => a.is_default);
    setSelectedAddress(defaultAddress ? defaultAddress.id : addresses[0].id);
  }, [addresses]);

  const handleChangeQuantity = useCallback(
    (item, newQuantity) => {
      if (!userLoggedIn) {
        const action = newQuantity > 0 ? CART_ACTION.SET_QUANTITY : CART_ACTION.REMOVE;
        dispatch(updateLocalCart({ action, itemPayload: { ...item, quantity: newQuantity } }));
      } else {
        dispatch(updateItemQuantityServer({ ...item, quantity: newQuantity, userLoggedIn }));
        setTimeout(() => dispatch(fetchCartFromServer()), 500);
      }
    },
    [dispatch]
  );

  const handleRemoveItem = useCallback(
    (item) => handleChangeQuantity(item, 0),
    [handleChangeQuantity]
  );

  const handleChangeSize = useCallback(() => {
    toast.warn("Remove item & add again to change size.");
  }, []);

  const activeTabIndex = useMemo(
    () => TABS.findIndex((t) => t.label === activeTab),
    [activeTab]
  );

  const nextTab = TABS[activeTabIndex + 1];

  const pricing = useMemo(
    () => ({
      subtotal: cartItems.reduce((sum, item) => sum + (item.variant.orignial_price || item.variant.display_price) * item.quantity, 0),
      discount: cartItems.reduce((sum, item) => sum + ((item.variant.orignial_price || item.variant.display_price) * item.quantity) , 0)- total,
      shipping: shipping_fee || 0,
      total: (final_total || total || 0),
    }),
    [total, discount, final_total, shipping_fee]
  );

  const CurrentComponent = useMemo(
    () => TABS.find((t) => t.label === activeTab)?.component,
    [activeTab]
  );



  const handleProceed = async () => {
    if (activeTab === "Bag") {
      if (!cartItems || cartItems.length === 0) {
        toast.error("Your bag is empty!");
        return;
      }
      if (nextTab) setActiveTab(nextTab.label);
      return;
    }

    if (activeTab === "Address") {
      if (!selectedAddress) {
        toast.error("Please select an address.");
        return;
      }

      const result = await dispatch(
        createOrder({
          address_id: selectedAddress,
          coupon,
          giftWrap,
        })
      );

      if (!createOrder.fulfilled.match(result)) {
   
        toast.error("Unable to place order.");
        return;
      }

      const data = result.payload;

      // Validate backend response
      if (!data?.razorpay_order_id || !data?.amount) {
        toast.error("Missing Razorpay payment data");
        return;
      }

      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        toast.error("Failed to load Razorpay SDK");
        return;
      }

      const options = {
        key: "rzp_live_Riioc9sJ9PI8Ov",
        amount: data.amount * 100, 
        currency: "INR",
        name: "AeliteZ Clothing",
        description: "Order Payment",
        order_id: data.razorpay_order_id,

        prefill: {
          name: data.customer_name,
          email: data.customer_email,
          contact: data.customer_phone,
        },

        handler: async function (payment) {
         
         
            toast.success("Payment Successful!");
            dispatch(clearLocalCartData());
            navigate("/account?tab=My Orders");
          
        },

        modal: {
          ondismiss: () => toast.warn("Payment cancelled."),
        },

        theme: {
          color: "#E53E3E",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    }
  };

  return (
    <>
      <Grid templateColumns={{ base: "1fr" }} gap={8} maxW="1200px" mx="auto" py="100px" px={4}>
        <ToastContainer />

        <VStack spacing={6} align="stretch">
          <Box display={{ base: "none", lg: "block" }}>
            <CheckoutStepper tabs={TABS} activeTab={activeTab} />
          </Box>

          <Box bg="white" border="1px solid #ddd" borderRadius="lg">
            <Box p={6}>
              <Suspense fallback={<Text>Loading...</Text>}>
                <CurrentComponent
                  cartItems={cartItems}
                  updateQuantity={handleChangeQuantity}
                  updateSize={handleChangeSize}
                  removeItem={handleRemoveItem}
                  setAddress={setSelectedAddress}
                  selectedAddress={selectedAddress}
                />
              </Suspense>
            </Box>

            <Flex p={4} bg="gray.50" justify="space-between">
              {activeTab !== "Bag" && (
                <Button onClick={() => setActiveTab("Bag")}>Back</Button>
              )}

              <Button colorScheme="blue" onClick={handleProceed}>
                {nextTab ? `Continue to ${nextTab.label}` : "Pay Now"}
              </Button>
            </Flex>
          </Box>
        </VStack>

        <Box>
          <Suspense fallback={<Text>Loading Summary...</Text>}>
            <OrderSummary
              pricing={pricing}
              coupon={coupon}
              setCoupon={setCoupon}
              giftWrap={giftWrap}
              setGiftWrap={setGiftWrap}
              onProceed={handleProceed}
              currentTab={activeTab}
            />
          </Suspense>
        </Box>
      </Grid>
    </>
  );
}
